import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { LoadingBarService } from '@/app/core/loading/loading-bar.service';

/**
 * Global loading bar — a thin, fixed strip across the top of the viewport that
 * tracks every in-flight HTTP request (wired by the `loadingInterceptor`).
 *
 * The  client showed an indeterminate Material `mat-progress-bar`; this is
 * the StatusNow 2.0 take: an on-brand navy→blue fill that trickles forward with
 * a glowing leading "peg", so the chrome reads as actively filling rather than
 * just spinning. Reference-counted state lives in {@link LoadingBarService}.
 *
 * Decorative (aria-hidden) — in-context spinners and content convey load state
 * to assistive tech; this is a supplementary visual cue.
 */
@Component({
  selector: 'status-now-loading-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'aria-hidden': 'true',
    '[style.opacity]': 'service.visible() ? 1 : 0',
  },
  template: `
    <div
      class="snlb__fill"
      [style.width.%]="widthPct()"
    >
      <div class="snlb__peg"></div>
    </div>
  `,
  styles: `
    :host {
      position: fixed;
      inset: 0 0 auto 0;
      /* Above the CDK overlay layer (1000) so request feedback during a dialog
         stays visible; harmless as a pointer-events:none top strip. */
      z-index: 1100;
      height: 3px;
      pointer-events: none;
      /* Fade the whole bar out once a batch of requests settles.
         Keep in sync with LoadingBarService.FADE_MS. */
      transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .snlb__fill {
      position: relative;
      height: 100%;
      border-radius: 0 3px 3px 0;
      /* Navy chrome filling into bright interactive blue. */
      background: linear-gradient(
        90deg,
        var(--color-navy-700),
        var(--color-brand) 65%,
        #5b8bff
      );
      box-shadow: 0 0 8px rgba(37, 99, 235, 0.45);
      /* Smoothly chase the trickling progress signal. */
      transition: width 220ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* The glowing tip riding the leading edge of the fill. */
    .snlb__peg {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 90px;
      transform: translateX(2px);
      border-radius: 9999px;
      background: linear-gradient(90deg, transparent, #7ba6ff);
      box-shadow:
        0 0 10px rgba(91, 139, 255, 0.9),
        0 0 4px rgba(91, 139, 255, 1);
      opacity: 0.85;
      animation: snlb-peg 1.1s ease-in-out infinite;
    }

    @keyframes snlb-peg {
      0%,
      100% {
        opacity: 0.55;
      }
      50% {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .snlb__peg {
        animation: none;
      }
    }
  `,
})
export class LoadingBar {
  protected readonly service = inject(LoadingBarService);

  protected readonly widthPct = computed(() => this.service.progress() * 100);
}
